import { useState, useEffect } from 'react';
import { useClaimDailyCoins } from '../../hooks/useInventory';
import { getErrorMessage } from '../../utils/errorMessages';
import { useProfile } from '../../hooks/useProfile';
import { AttendanceCheck } from './AttendanceCheck';
import { CardPackSlots } from './CardPackSlots';
import { UserProfile } from '../profile/UserProfile';
import './DailyActionsPanel.css';

interface DailyActionsPanelProps {
  isAuthenticated: boolean;
  currency?: number;
  onGameStart?: () => void;
  onRankingClick?: () => void;
}

export function DailyActionsPanel({
  isAuthenticated,
  currency,
  onGameStart,
  onRankingClick,
}: DailyActionsPanelProps) {
  const [coinsMessage, setCoinsMessage] = useState<string | null>(null);
  const [arenaMessage, setArenaMessage] = useState<string | null>(null);
  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const { data: profile } = useProfile();
  const claimMutation = useClaimDailyCoins();
  const isClaiming = claimMutation.isPending;

  // 출석체크 관련 상태
  const attendanceStreak = profile?.attendance_streak || 0;
  const lastAttendanceDate = profile?.last_attendance_date;
  const today = new Date().toISOString().split('T')[0];
  const isCheckedToday = lastAttendanceDate === today;
  const todayReward = (attendanceStreak + 1) % 7 === 0 ? 5 : 1;

  const handleClaim = async () => {
    setCoinsMessage(null);
    try {
      const result = await claimMutation.mutateAsync();
      setCoinsMessage(
        `오늘 커밋으로 재화 ${result.coinsEarned}개 획득! 총 ${result.total.toLocaleString()}개 보유`
      );
    } catch (error) {
      setCoinsMessage(getErrorMessage(error));
    }
  };

  const handleAttendanceButtonClick = () => {
    setIsAttendanceModalOpen(true);
  };

  const handleCloseAttendanceModal = () => {
    setIsAttendanceModalOpen(false);
  };

  const handleArenaStart = () => {
    onGameStart?.();
  };

  // 메시지 자동 사라짐
  useEffect(() => {
    if (coinsMessage) {
      const timer = setTimeout(() => setCoinsMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [coinsMessage]);

  // 모달 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (isAttendanceModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isAttendanceModalOpen]);

  const disabledReason = !isAuthenticated
    ? 'GitHub로 로그인 후 이용해주세요.'
    : undefined;

  return (
    <section className="daily-actions">
      {/* 프로필 컴포넌트 */}
      {isAuthenticated && <UserProfile />}
      
      {/* 상단 두 개의 버튼 */}
      {isAuthenticated && (
        <div className="daily-actions__quick-buttons">
          {/* 출석체크 버튼 */}
          <div className="quick-button-container">
            <button
              type="button"
              className="quick-button quick-button--attendance"
              onClick={handleAttendanceButtonClick}
              title={isCheckedToday ? '오늘 출석체크 완료' : `오늘 보상: +${todayReward} 골드`}
            >
              <div className="quick-button__icon">📅</div>
              <div className="quick-button__content">
                <div className="quick-button__title">출석체크</div>
                <div className="quick-button__subtitle">
                  {isCheckedToday ? '완료 ✓' : `연속 ${attendanceStreak}일`}
                </div>
              </div>
            </button>
          </div>

          {/* 재화 받기 버튼 */}
          <div className="quick-button-container">
            <button
              type="button"
              className="quick-button quick-button--coins"
              onClick={handleClaim}
              disabled={!isAuthenticated || isClaiming}
              title={disabledReason}
            >
              <img src="/coin.png" alt="재화" className="quick-button__icon-img" />
              <div className="quick-button__content">
                <div className="quick-button__title">재화 받기</div>
                <div className="quick-button__subtitle">
                  {isClaiming ? '수집 중...' : '커밋 보상'}
                </div>
              </div>
            </button>
            {coinsMessage && (
              <div className="quick-button__message">{coinsMessage}</div>
            )}
          </div>
            </div>
      )}

      {/* 아레나 배틀 섹션 */}
      <div className="arena-tab-content">
        <div className="arena-center">
          <div className="arena-image-container">
            <img 
              src="/arena_asset_free.webp" 
              alt="아레나" 
              className="arena-image"
            />
          </div>
          <button
            type="button"
            className="arena-start-button"
            onClick={handleArenaStart}
          >
            게임 시작하기
          </button>
          {arenaMessage && (
            <div className="arena-message">{arenaMessage}</div>
          )}
        </div>
        
        {/* 카드팩 슬롯 - 하단 고정 */}
        <div className="arena-card-packs">
          <CardPackSlots />
        </div>
      </div>

      {!isAuthenticated && (
        <div className="daily-actions__warning">
          GitHub로 로그인하면 데일리 보상과 카드 소환을 이용할 수 있습니다.
        </div>
      )}

      {/* 플로팅 랭킹 버튼 */}
      {isAuthenticated && onRankingClick && (
        <button
          className="floating-ranking-button"
          onClick={onRankingClick}
          aria-label="랭킹"
          type="button"
        >
          <span className="floating-ranking-button__icon">🏆</span>
        </button>
      )}

      {/* 출석체크 모달 */}
      {isAttendanceModalOpen && (
        <div className="attendance-modal-overlay" onClick={handleCloseAttendanceModal}>
          <div className="attendance-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="attendance-modal__close" 
              onClick={handleCloseAttendanceModal}
              aria-label="닫기"
            >
              ×
            </button>
            <AttendanceCheck />
          </div>
        </div>
      )}
    </section>
  );
}


